import React, { useState, useEffect } from 'react';
import { XMarkIcon, XCircleIcon } from '@heroicons/react/24/outline';
import {
  OrderResponse,
  OrderStatus,
} from '../../types/orders';
import { Image } from 'antd';
import { Row, Col, Button, message, notification, Modal } from 'antd';
import orderService from '../../services/orderService';



interface OrderDetailModalProps {
  order: OrderResponse | null;
  onClose: () => void;
  onUpdateStatus: (
    orderId: string,
    newStatus: OrderStatus,
    additionalData?: any
  ) => void;
}

const OrderDetailModal: React.FC<OrderDetailModalProps> = ({
  order,
  onClose,
  onUpdateStatus,
}) => {
  console.log('order', order);
  const [currentOrder, setCurrentOrder] = useState<OrderResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');
  
  // Reject modal states
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  
  // Cancel modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');



  // Khởi tạo các giá trị ban đầu khi order thay đổi
  useEffect(() => {
    if (order) {
      setCurrentOrder(order);
      setOrderNotes(order.note || '');
    }
  }, [order]);

  if (!currentOrder) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Intl.DateTimeFormat('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(date));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'shipped':
        return 'bg-orange-100 text-orange-800';
      case 'delivered':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Chờ xử lý';
      case 'processing':
        return 'Đang xử lý';
      case 'shipped':
        return 'Đã giao hàng';
      case 'delivered':
        return 'Hoàn thành';
      case 'cancelled':
        return 'Đã hủy';
      case 'rejected':
        return 'Từ chối nhận hàng';
      default:
        return status;
    }
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-orange-100 text-orange-800';
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPaymentStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Chờ thanh toán';
      case 'paid':
        return 'Đã thanh toán';
      case 'failed':
        return 'Thanh toán thất bại';
      default:
        return status;
    }
  };

  // Tính toán tổng giá mới khi có thay đổi shipping_fee hoặc surcharge
  const calculateNewTotal = (newShippingFee?: number, newSurcharge?: number) => {
    const currentPriceDetail = currentOrder.priceDetail;
    const newShippingFeeValue = newShippingFee ?? currentPriceDetail.shippingFee;
    const newSurchargeValue = newSurcharge ?? 0;
    
    return currentPriceDetail.productPrice - currentPriceDetail.discountPrice + newShippingFeeValue + newSurchargeValue;
  };

  const simulateStatusUpdate = async (
    newStatus: OrderStatus,
    additionalData?: any
  ) => {
    setLoading(true);

    try {
      // Tính toán tổng giá mới nếu có thay đổi
      let newTotalPrice = currentOrder.priceDetail.totalPrice;
      let updatedPriceDetail = { ...currentOrder.priceDetail };

      if (additionalData?.shippingFee !== undefined) {
        updatedPriceDetail.shippingFee = additionalData.shippingFee;
        newTotalPrice = calculateNewTotal(additionalData.shippingFee, additionalData.surcharge);
      }

      if (additionalData?.surcharge !== undefined) {
        newTotalPrice = calculateNewTotal(updatedPriceDetail.shippingFee, additionalData.surcharge);
      }

      updatedPriceDetail.totalPrice = newTotalPrice;

    const updatedOrder = {
      ...currentOrder,
        orderStatus: newStatus,
        priceDetail: updatedPriceDetail,
    } as any;

    if (additionalData) {
      if (additionalData.deliveryCode) {
          updatedOrder.note = additionalData.deliveryCode;
      }
    }

    setCurrentOrder(updatedOrder);
      onUpdateStatus(updatedOrder._id, newStatus, additionalData);
      
      message.success(`Cập nhật trạng thái đơn hàng thành công!`);
    } catch (error) {
      message.error('Có lỗi xảy ra khi cập nhật trạng thái đơn hàng');
      console.error('Error updating order status:', error);
    } finally {
    setLoading(false);
    }
  };

  // Lấy trạng thái tiếp theo có thể chuyển đến
  const getAvailableNextStatuses = (currentStatus: OrderStatus): OrderStatus[] => {
    switch (currentStatus) {
      case 'pending':
        return ['processing', 'cancelled'];
      case 'processing':
        return ['shipped', 'cancelled'];
      case 'shipped':
        return ['delivered', 'cancelled', 'rejected'];
      case 'delivered':
        return [];
      case 'cancelled':
        return [];
      case 'rejected':
        return [];
      default:
        return [];
    }
  };

  // Lấy text cho action button
  const getStatusActionText = (status: OrderStatus): string => {
    switch (status) {
      case 'processing':
        return 'Xác nhận đơn hàng';
      case 'shipped':
        return 'Đã giao hàng';
      case 'delivered':
        return 'Hoàn thành';
      case 'cancelled':
        return 'Hủy đơn hàng';
      case 'rejected':
        return 'Từ chối nhận hàng';
      default:
        return 'Cập nhật trạng thái';
    }
  };

  // Lấy màu cho button
  const getButtonStyleByStatus = (status: OrderStatus): React.CSSProperties => {
    switch (status) {
      case 'processing':
        return { backgroundColor: '#1890ff', borderColor: '#1890ff' }; // Xanh dương
      case 'shipped':
        return { backgroundColor: '#fa8c16', borderColor: '#fa8c16' }; // Cam
      case 'delivered':
        return { backgroundColor: '#52c41a', borderColor: '#52c41a' }; // Xanh lá
      case 'cancelled':
      case 'rejected':
        return { backgroundColor: '#ff4d4f', borderColor: '#ff4d4f' }; // Đỏ
      default:
        return {};
    }
  };

  // Xử lý khi click vào button chuyển trạng thái
  const handleStatusChange = (newStatus: OrderStatus) => {
    // Kiểm tra các trường bắt buộc trước khi cập nhật
    let additionalData: any = {};

    switch (newStatus) {
      case 'shipped':
        // Tạm thời bỏ kiểm tra phí vận chuyển
        // if (currentOrder.priceDetail.shippingFee <= 0) {
        //   message.error('Phí vận chuyển phải lớn hơn 0 khi chuyển sang trạng thái "Đã giao hàng"');
        //   return;
        // }
        additionalData.shippingFee = currentOrder.priceDetail.shippingFee;
        break;

      case 'cancelled':
        // Mở modal hủy đơn hàng
        setShowCancelModal(true);
        return;
        
      case 'rejected':
        setShowRejectModal(true);
        return;
    }

    // Cập nhật trực tiếp không cần popup
    simulateStatusUpdate(newStatus, additionalData);
  };

  // Xử lý từ chối nhận hàng
  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) {
      message.error('Vui lòng nhập lý do từ chối nhận hàng');
      return;
    }

    const additionalData = {
      reason: rejectReason,
    };

    simulateStatusUpdate('rejected', additionalData);
    setShowRejectModal(false);
    setRejectReason('');
  };

  // Xử lý hủy đơn hàng
  const handleCancelSubmit = () => {
    if (!cancelReason.trim()) {
      message.error('Vui lòng nhập lý do hủy đơn hàng');
      return;
    }

    const additionalData = {
      reason: cancelReason,
    };

    simulateStatusUpdate('cancelled', additionalData);
    setShowCancelModal(false);
    setCancelReason('');
  };

  

  const handleUpdateOrderNotes = async () => {
    try {
      await orderService.updateOrderNotes(currentOrder._id, orderNotes);
      setIsEditingNotes(false);
      setOrderNotes(orderNotes);
      message.success('Cập nhật ghi chú thành công!');
    } catch (error) {
      console.error('Error updating order notes:', error);
      message.error('Có lỗi xảy ra khi cập nhật ghi chú');
    }
  };

  const availableNextStatuses = getAvailableNextStatuses(currentOrder.orderStatus);

  return (
    <>
      <div
        className='flex overflow-y-auto fixed inset-0 z-50 justify-center items-center w-full h-full bg-gray-600 bg-opacity-50'
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
      <div className='relative bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto'>
        <button
          onClick={onClose}
            className='absolute top-4 right-4 text-gray-500 hover:text-gray-700'
        >
          <XMarkIcon className='w-6 h-6' />
        </button>

        <div className='p-6'>
            <div className='flex justify-between items-start'>
            <div>
              <h2 className='text-xl font-bold text-gray-900'>
                  {currentOrder.orderCode}
              </h2>
              <p className='text-sm text-gray-500'>
                  {formatDate(currentOrder.createdAt)}
              </p>
            </div>
          </div>

          <div className='grid grid-cols-1 gap-6 mt-6 md:grid-cols-2'>
            <div>
              <h3 className='text-lg font-medium text-gray-900'>
                Thông tin khách hàng
              </h3>
              <div className='mt-2 space-y-1'>
                <div className='grid grid-cols-3 gap-4'>
                  <p className='font-medium text-gray-700'>Họ tên:</p>
                  <p className='col-span-2 text-gray-700'>
                      {currentOrder.fullName}
                  </p>
                </div>
                <div className='grid grid-cols-3 gap-4'>
                  <p className='font-medium text-gray-700'>Email:</p>
                  <p className='col-span-2 text-gray-700'>
                      {currentOrder.email}
                  </p>
                </div>
                <div className='grid grid-cols-3 gap-4'>
                    <p className='font-medium text-gray-700'>Số điện thoại:</p>
                  <p className='col-span-2 text-gray-700'>
                      {currentOrder.phone}
                  </p>
                </div>
                <div className='grid grid-cols-3 gap-4'>
                  <p className='font-medium text-gray-700'>Địa chỉ:</p>
                  <p className='col-span-2 text-gray-700 whitespace-pre-line'>
                      {currentOrder.shippingAddress}
                  </p>
                </div>
                  {currentOrder.note && (
                  <div className='grid grid-cols-3 gap-4'>
                    <p className='font-medium text-gray-700'>Ghi chú:</p>
                    <p className='col-span-2 text-gray-700'>
                        {currentOrder.note}
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h3 className='text-lg font-medium text-gray-900'>Trạng thái</h3>
              <div className='mt-2 space-y-2'>
                <p className='text-gray-700'>
                  <span className='font-medium'>Trạng thái đơn hàng: </span>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        currentOrder.orderStatus
                    )}`}
                  >
                      {getStatusText(currentOrder.orderStatus)}{' '}
                  </span>
                </p>
                    <p className='text-gray-700'>
                    <span className='font-medium'>Trạng thái thanh toán: </span>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPaymentStatusColor(
                        currentOrder.paymentStatus
                      )}`}
                    >
                      {getPaymentStatusText(currentOrder.paymentStatus)}{' '}
                    </span>
                  </p>
                  <p className='text-gray-700'>
                    <span className='font-medium'>Phương thức thanh toán: </span>
                    <span className='text-gray-700'>
                      {currentOrder.paymentMethod}
                                </span>
                  </p>
                  {currentOrder.paymentNode && (
                    <p className='text-gray-700'>
                      <span className='font-medium'>Ghi chú thanh toán: </span>
                      <span className='text-gray-700'>
                        {currentOrder.paymentNode}
                                    </span>
                    </p>
                  )}
                  {currentOrder.paymentCode && (
                    <p className='text-gray-700'>
                      <span className='font-medium'>Mã thanh toán: </span>
                      <span className='text-gray-700'>
                        {currentOrder.paymentCode}
                      </span>
                    </p>
                  )}
                  {currentOrder.metaData?.vnPayCreateDate && (
                    <p className='text-gray-700'>
                      <span className='font-medium'>Thời gian thanh toán VNPay: </span>
                      <span className='text-gray-700'>
                        {formatDate(currentOrder.metaData.vnPayCreateDate)}
                      </span>
                    </p>
                  )}
                  {currentOrder.orderStatus === 'cancelled' && currentOrder.cancelReason && (
                    <p className='text-gray-700'>
                      <span className='font-medium'>Lý do hủy đơn: </span>
                      <span className='text-red-600'>
                        {currentOrder.cancelReason}
                      </span>
                    </p>
                  )}
                  {currentOrder.orderStatus === 'rejected' && currentOrder.rejectionReason && (
                    <p className='text-gray-700'>
                      <span className='font-medium'>Lý do từ chối nhận hàng: </span>
                      <span className='text-red-600'>
                        {currentOrder.rejectionReason}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            </div>

          <div className='mt-6'>
              <div className='flex justify-between items-center'>
                <h3 className='text-lg font-medium text-gray-900'>Sản phẩm</h3>
            </div>
            <div className='pt-5 mt-2 border-t border-gray-200'>
              <div className='flow-root'>
                <ul className='-my-6 divide-y divide-gray-200'>
                    {currentOrder.products.map((product, index) => (
                      <li key={index} className='flex py-6'>
                        <div className='overflow-hidden flex-shrink-0 w-20 h-20 bg-gray-200 rounded-md'>
                          <Image
                            src={product.selectedVariant?.thumbnail_url || product.selectedColorVariant?.image_url || product.productId?.images?.[0] || 'https://fujifruit.com.vn/wp-content/uploads/2022/12/Untitled-design-10-1.png'}
                            alt={product.name}
                            width={80}
                            height={80}
                            style={{ objectFit: 'cover' }}
                          />
                        </div>
                        <div className='flex flex-col flex-1 ml-4'>
                          <div>
                            <div className='flex justify-between text-base font-medium text-gray-900'>
                              <h3>{product.name}</h3>
                              <p className='ml-4'>
                                {formatCurrency(product.price * product.quantity)}
                              </p>
                            </div>
                            <p className='mt-1 text-sm text-gray-500'>
                              {formatCurrency(product.price)} x {product.quantity}
                            </p>
                            
                            {/* Thông tin chi tiết sản phẩm */}
                            <div className='mt-2 space-y-1'>
                              {product.selectedVariant && (
                                <div className='space-y-1'>
                                  <div className='flex items-center space-x-2'>
                                    <span className='text-sm font-medium text-gray-600'>Phân loại:</span>
                                    <span className='text-sm text-gray-700'>{product.selectedVariant.title}</span>
                                  </div>
                                  {/* {product.selectedVariant.description && (
                                    <div className='flex items-start space-x-2'>
                                      <span className='text-sm font-medium text-gray-600'>Mô tả:</span>
                                      <span className='text-sm text-gray-700'>{product.selectedVariant.description}</span>
                                    </div>
                                  )} */}
                                </div>
                              )}
                              
                              {product.selectedColorVariant && (
                                <div className='flex items-center space-x-2'>
                                  <span className='text-sm font-medium text-gray-600'>Phân loại:</span>
                                  <div className='flex items-center space-x-2'>
                                    <div 
                                      className='w-4 h-4 rounded-full border border-gray-300'
                                      style={{ 
                                        backgroundColor: product.selectedColorVariant.name.toLowerCase().includes('xanh') ? '#3B82F6' : 
                                                       product.selectedColorVariant.name.toLowerCase().includes('nâu') ? '#8B4513' : '#6B7280'
                                      }}
                                    />
                                    <span className='text-sm text-gray-700'>{product.selectedColorVariant.name}</span>
                                  </div>
                                </div>
                              )}
                              
                              {product.selectedSize && (
                                <div className='flex items-center space-x-2'>
                                  <span className='text-sm font-medium text-gray-600'>Kích thước:</span>
                                  <span className='text-sm text-gray-700'>{product.selectedSize.label}</span>
                                </div>
                              )}
                              
                              {product.productId?.price && (
                                <div className='flex items-center space-x-2'>
                                  <span className='text-sm font-medium text-gray-600'>Giá gốc:</span>
                                  <span className='text-sm text-gray-500 line-through'>
                                    {formatCurrency(product.productId.price.regular)}
                                  </span>
                                  <span className='text-sm font-medium text-green-600'>
                                    {formatCurrency(product.productId.price.sale)}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                </ul>
              </div>
            </div>
          </div>

          <div className='mt-6'>
            <div className='pt-4 border-t border-gray-200'>
              <div className='flex justify-between text-base text-gray-700'>
                <p>Tổng giá trị đơn hàng</p>
                  <p>{formatCurrency(currentOrder.priceDetail.productPrice)}</p>
              </div>
              <div className='flex justify-between text-base text-gray-700'>
                <p>Giảm giá</p>
                <p>
                    {formatCurrency(currentOrder.priceDetail.discountPrice)}
                </p>
              </div>
              <div className='flex justify-between mt-2 text-base text-gray-700'>
                <p>Phí vận chuyển</p>
                  <p>{formatCurrency(currentOrder.priceDetail.shippingFee)}</p>
              </div>
              {currentOrder.priceDetail.surcharge > 0 && (
                <div className='flex justify-between mt-2 text-base text-gray-700'>
                  <p>Phí phụ thu</p>
                  <p>{formatCurrency(currentOrder.priceDetail.surcharge)}</p>
                </div>
              )}
              <div className='flex justify-between mt-4 text-lg font-medium text-gray-900'>
                <p>Tổng cộng</p>
                  <p>{formatCurrency(currentOrder.priceDetail.totalPrice)}</p>
              </div>
            </div>
          </div>

            {/* Thao tác cập nhật trạng thái */}
            {availableNextStatuses.length > 0 && (
            <div className='pt-4 my-4 border-t'>
              <h3 className='mb-3 text-lg font-medium'>Thao tác</h3>
              <Row gutter={16}>
                  {availableNextStatuses.map((status) => (
                    <Col key={status} span={8}>
                  <Button
                    type='primary'
                        onClick={() => handleStatusChange(status)}
                    loading={loading}
                    block
                        style={getButtonStyleByStatus(status)}
                  >
                        {getStatusActionText(status)}
                  </Button>
                </Col>
                  ))}
              </Row>
            </div>
          )}

          {/* Tạm thời comment lại tính năng ghi chú đơn hàng
          Ghi chú đơn hàng
          <div className='mt-6'>
              <div className='flex justify-between items-center'>
              <h3 className='text-lg font-medium text-gray-900'>
                Ghi chú đơn hàng
              </h3>
              {!isEditingNotes ? (
                <button
                  onClick={() => {
                    setIsEditingNotes(true);
                      setOrderNotes(currentOrder.note || '');
                  }}
                  className='p-1 text-gray-500 hover:text-gray-700'
                  title='Chỉnh sửa ghi chú'
                >
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    className='w-5 h-5'
                    viewBox='0 0 20 20'
                    fill='currentColor'
                  >
                    <path d='M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z' />
                  </svg>
                </button>
              ) : (
                <button
                  onClick={() => {
                    setIsEditingNotes(false);
                    handleUpdateOrderNotes();
                  }}
                  className='px-3 py-1 text-sm text-white bg-blue-600 rounded-md hover:bg-blue-700'
                >
                  Cập nhật
                </button>
              )}
            </div>
              <div className='p-4 mt-2 bg-gray-50 rounded-md'>
              {isEditingNotes ? (
                <textarea
                  value={orderNotes}
                  onChange={(e) => setOrderNotes(e.target.value)}
                    className='p-2 w-full rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500'
                  rows={4}
                  placeholder='Nhập ghi chú đơn hàng...'
                />
              ) : orderNotes ? (
                <p className='text-gray-700 whitespace-pre-wrap'>
                  {orderNotes}
                </p>
              ) : (
                <p className='italic text-gray-500'>Chưa có ghi chú</p>
              )}
            </div>
          </div>
          */}
        </div>
      </div>
    </div>

      {/* Modal từ chối nhận hàng */}
      <Modal
        title="Từ chối nhận hàng"
        open={showRejectModal}
        onOk={handleRejectSubmit}
        onCancel={() => {
          setShowRejectModal(false);
          setRejectReason('');
        }}
        confirmLoading={loading}
        okText="Xác nhận từ chối"
        cancelText="Hủy"
        width={600}
      >
        <div>
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">
              Lý do từ chối nhận hàng <span className="text-red-500">*</span>
            </label>
            <textarea
              className="p-2 w-full rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              placeholder="Nhập lý do từ chối nhận hàng"
            />
          </div>

        </div>
      </Modal>

      {/* Modal hủy đơn hàng */}
      <Modal
        title="Hủy đơn hàng"
        open={showCancelModal}
        onOk={handleCancelSubmit}
        onCancel={() => {
          setShowCancelModal(false);
          setCancelReason('');
        }}
        confirmLoading={loading}
        okText="Xác nhận hủy"
        cancelText="Hủy"
        width={600}
      >
        <div>
          <div className="mb-4">
            <label className="block mb-2 text-sm font-medium">
              Lý do hủy đơn hàng <span className="text-red-500">*</span>
            </label>
            <textarea
              className="p-2 w-full rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={3}
              placeholder="Nhập lý do hủy đơn hàng"
            />
          </div>
        </div>
      </Modal>

    </>
  );
};

export default OrderDetailModal;
